import { updateURLs } from '../url';
import SearchThisArea from './components/search-this-area';
import { parseURL } from '../url/parse';
import MapSearch from './search-map';

const facetSuffixs = [
    'Region',
    'Province',
    'Island',
    'Coast',
    'Municipality',
    'District',
    'Neighbourhood',
    'Entity',
    'Nearby',
];

type UIState = {
    h1?: string;
    propertyCount?: number;
    propertyCountFormatted?: string;
    breadcrumbHTML?: string;
    saveSearchValue?: string;
    saveSearchMeta?: string;
    saveSearchHandler?: string;
    saveSearchRequiresHandler?: string;
    searchURLs?: Record<string, string>;
    enableRadius: boolean;
    locationInputValue?: string;
    locationInputText?: string;
    locationInputIsGeocoded: boolean;
    locationInputIsIsland: boolean;
    locationInputLocationType?: string;
    location?: any;
    addToWishlistEvent?: string;
}

class SearchUI {
    _ctx: MapSearch;

    _facetsRegion: HTMLElement;
    _facetsProvince: HTMLElement;
    _facetsIsland: HTMLElement;
    _facetsCoast: HTMLElement;
    _facetsMunicipality: HTMLElement;
    _facetsDistrict: HTMLElement;
    _facetsNeighbourhood: HTMLElement;
    _facetsEntity: HTMLElement;
    _facetsNearby: HTMLElement;
    _h1: HTMLHeadingElement;
    _breadcrumbs: NodeListOf<HTMLOListElement>;
    _saveSearchBtns: NodeListOf<HTMLButtonElement>;
    _facetsBtnDesktop: HTMLButtonElement;
    _facetsBtnMobile: HTMLButtonElement;
    _radiusContainer: HTMLElement;
    _radius: HTMLInputElement;
    _searchCounts: NodeListOf<HTMLOListElement>;
    _searchCountsTickers:  NodeListOf<HTMLElement>;
    _currencyRedirectLinks: NodeListOf<HTMLAnchorElement>;
    _languageRedirectLinks: NodeListOf<HTMLAnchorElement>;
    _popupLocationResetBtn: HTMLButtonElement;
    _locationIds: NodeListOf<HTMLInputElement>;

    constructor( ctx: MapSearch ) {
        this._ctx = ctx;

        this._facetsRegion = document.querySelector<HTMLElement>( '#region-facets' ) as HTMLElement;
        this._facetsProvince = document.querySelector<HTMLElement>( '#province-facets' ) as HTMLElement;
        this._facetsIsland = document.querySelector<HTMLElement>( '#island-facets' ) as HTMLElement;
        this._facetsCoast = document.querySelector<HTMLElement>( '#coast-facets' ) as HTMLElement;
        this._facetsMunicipality = document.querySelector<HTMLElement>( '#municipality-facets' ) as HTMLElement;
        this._facetsDistrict = document.querySelector<HTMLElement>( '#district-facets' ) as HTMLElement;
        this._facetsNeighbourhood = document.querySelector<HTMLElement>( '#neighbourhood-facets' ) as HTMLElement;
        this._facetsEntity = document.querySelector<HTMLElement>( '#entity-facets' ) as HTMLElement;
        this._facetsNearby = document.querySelector<HTMLElement>( '#nearby-facets' ) as HTMLElement;
        this._h1 = document.querySelector<HTMLHeadingElement>( 'h1' ) as HTMLHeadingElement;
        this._breadcrumbs = document.querySelectorAll<HTMLOListElement>( '.search-intro .search-breadcrumb' );
        this._saveSearchBtns = document.querySelectorAll<HTMLButtonElement>( '.twc__savable--search' );
        this._facetsBtnDesktop = document.querySelector<HTMLButtonElement>( '#region-board-opener' ) as HTMLButtonElement;
        this._facetsBtnMobile = document.querySelector<HTMLButtonElement>( '#locations-panel-header-mobile' ) as HTMLButtonElement;
        this._radiusContainer = document.querySelector<HTMLElement>( '.searchpopup__location-radius' ) as HTMLElement;
        this._radius = document.querySelector<HTMLInputElement>( '[name=radius]' ) as HTMLInputElement;
        this._searchCounts = document.querySelectorAll<HTMLOListElement>( '.count' );
        this._searchCountsTickers = document.querySelectorAll<HTMLElement>( '.count-number' );
        this._currencyRedirectLinks = document.querySelectorAll<HTMLAnchorElement>( '#currency-mobile a' );
        this._languageRedirectLinks = document.querySelectorAll<HTMLAnchorElement>( '#language-mobile a' );
        this._popupLocationResetBtn = ctx.popupForm.querySelector<HTMLButtonElement>( '.reset-field[data-target-input=locationFreeText]' ) as HTMLButtonElement;
        this._locationIds = document.querySelectorAll<HTMLInputElement>( '.property-search-form input[name=locationId]' );

        document.addEventListener( 'twc:search-ui-update', this.handleUpdate.bind( this ) );
    }

    /**
     * handle the initial event triggered by passing data to update
     * @param {Event} event - the CustomEvent triggered
     * @returns void
     */
    handleUpdate( event: CustomEvent ): void {
        const { data, updateContent } = event.detail;
        this.update( updateContent, data );
    }

    update( updateContent = true, state: UIState ): void {
        if ( state.propertyCountFormatted && state.propertyCountFormatted.length > 0 ) {
            for ( let i = 0; i < this._searchCounts.length; i++ ) {
                this._searchCounts[ i ].innerHTML = state.propertyCountFormatted as string;
            }

            for ( let i = 0; i < this._searchCountsTickers.length; i++ ) {
                if ( this._ctx.currentLocation && this._ctx.currentLocation.id > 0 ) {
                    this._searchCountsTickers[ i ].innerHTML = state.propertyCountFormatted as string;
                }
            }
        }

        if ( state.propertyCount ) {
            const formsWithPropertyCountAttr = document.querySelectorAll( 'form[data-property-count]' );
            formsWithPropertyCountAttr.forEach( ( form: HTMLFormElement ) => form.dataset.propertyCount = ( state.propertyCount as number ).toString() );
        }

        if ( state.h1 && state.h1.length > 0 ) {
            this._h1.innerHTML = state.h1;
        }

        if ( state.addToWishlistEvent ) {
            for ( let i = 0; i < this._saveSearchBtns.length; i++ ) {
                this._saveSearchBtns[ i ].dataset.addToWishlistEvent = state.addToWishlistEvent;
            }
        }

        if ( state.saveSearchValue && state.saveSearchMeta ) {
            for ( let i = 0; i < this._saveSearchBtns.length; i++ ) {
                this._saveSearchBtns[ i ].dataset.saveValue = state.saveSearchValue;
                this._saveSearchBtns[ i ].dataset.saveMeta = state.saveSearchMeta;
            }

            document.dispatchEvent( new CustomEvent( 'twc:update-saved-status', {
                bubbles: true,
                detail: {
                    container: document.body,
                },
            } ) );
        }

        if ( state.saveSearchRequiresHandler ) {
            for ( let i = 0; i < this._saveSearchBtns.length; i++ ) {
                this._saveSearchBtns[ i ].dataset.requiresHandler = state.saveSearchRequiresHandler;
                this._saveSearchBtns[ i ].dataset.handler = state.saveSearchHandler || '';
            }
        }

        if ( state.breadcrumbHTML && this._breadcrumbs.length > 0 ) {
            const newBreadcrumbs = document.createElement( 'div' );
            newBreadcrumbs.innerHTML = state.breadcrumbHTML;

            const newCrumbs = newBreadcrumbs.querySelectorAll( 'li' );

            this._breadcrumbs.forEach( ( list: HTMLOListElement ) => {
                list.querySelectorAll( '.crumb' ).forEach( ( crumb: HTMLLIElement ) => {
                    if ( crumb.classList.contains( 'expander' ) ) {
                        return;
                    }
                    crumb.remove();
                } );

                for ( let i = newCrumbs.length - 1; i >= 0; i-- ) {
                    list.insertAdjacentHTML( 'afterbegin', newCrumbs[ i ].outerHTML );
                }
            } );
        }

        if ( state.searchURLs ) {
            for ( const [ lang, path ] of Object.entries( state.searchURLs ) ) {
                const url = parseURL( path );

                if ( this._ctx.activeComponents.has( SearchThisArea.id ) ) {
                    url.searchParams.set( 'viewmode', SearchThisArea.id );
                }

                const urlPath = url.pathname + url.search;

                if ( lang === this._ctx.language ) {
                    updateURLs( '#currency-mobile a', 'returnUrl', urlPath );
                    updateURLs( '.header__currencychange a', 'returnUrl', urlPath );
                }

                updateURLs( `#language-mobile a[data-language=${lang}]`, 'returnUrl', urlPath );
            }
        }

        if ( state.enableRadius && updateContent ) {
            this._radiusContainer.classList.remove( 'd-none' );
            this._radius.disabled = false;
        }
        if ( !state.enableRadius && updateContent ) {
            this._radiusContainer.classList.add( 'd-none' );
            this._radius.disabled = true;
        }

        const updateTypeaheadLocation: boolean = (
            'locationInputText' in state &&
            'locationInputValue' in state
        );

        const locationKeys: NodeListOf<HTMLInputElement> = document.querySelectorAll( '.property-search-form [name="locationId"]' );
        const locationTexts: NodeListOf<HTMLInputElement> = document.querySelectorAll( '.property-search-form [name="locationFreeText"]' );

        const alternativeValues: Array<string> = [ 'map-area', 'search-this-area', 'draw-your-search', 'location-filters' ];

        const locationValueIsValid = (
            state.locationInputValue &&
            (
                parseInt( state.locationInputValue ) > 0 ||
                alternativeValues.includes( state.locationInputValue as string ) ||
                state.locationInputValue.startsWith( 'location-filters' )
            )
        );

        if ( updateTypeaheadLocation && updateContent && locationValueIsValid ) {
            for ( let t = 0; t < locationTexts.length; t++ ) {
                locationTexts[ t ].value = state.locationInputText as string;
                ( ( ( locationTexts[ t ].parentElement as HTMLElement ).parentElement as HTMLElement ).querySelector( '.reset-field' ) as HTMLElement ).style.display = '';
                locationTexts[ t ].dataset.tsGeo = state.locationInputIsGeocoded ? state.locationInputIsGeocoded.toString() : 'false';
            }

            for ( let k = 0; k < locationKeys.length; k++ ) {
                locationKeys[ k ].value = state.locationInputValue as string;
                locationKeys[ k ].dataset.isGeocoded = state.locationInputIsGeocoded ? state.locationInputIsGeocoded.toString() : 'false';
                locationKeys[ k ].dataset.isIsland = state.locationInputIsIsland ? state.locationInputIsIsland.toString() : 'false';
                locationKeys[ k ].dataset.id = state.locationInputValue || '';
                locationKeys[ k ].dataset.type = state.locationInputLocationType || '';
                locationKeys[ k ].dataset.location = state.location ? JSON.stringify( state.location ) : '';
            }
        }

        if ( !updateTypeaheadLocation && updateContent ) {
            for ( let t = 0; t < locationTexts.length; t++ ) {
                locationTexts[ t ].value = '';
                ( ( ( locationTexts[ t ].parentElement as HTMLElement ).parentElement as HTMLElement ).querySelector( '.reset-field' ) as HTMLElement ).style.display = 'none';
                locationTexts[ t ].dataset.tsGeo = '';
            }

            for ( let k = 0; k < locationKeys.length; k++ ) {
                locationKeys[ k ].value = '';
                locationKeys[ k ].dataset.isGeocoded = '';
                locationKeys[ k ].dataset.isIsland = '';
                locationKeys[ k ].dataset.id = '';
                locationKeys[ k ].dataset.type = '';
                locationKeys[ k ].dataset.latitude = '';
                locationKeys[ k ].dataset.longitude = '';
                locationKeys[ k ].dataset.location = '';
            }
        }

        if ( updateContent ) {
            let showFacetsBtn = false;

            for ( let suffixIndex = 0; suffixIndex < facetSuffixs.length; suffixIndex++ ) {
                const v = `facets${facetSuffixs[ suffixIndex ]}`;

                if ( !( v in state ) ) {
                    continue;
                }

                // this[ v ] is like this._facetsRegion etc.
                const facetContainer: HTMLElement = this[ `_${v}` ];

                if ( !facetContainer ) {
                    throw new Error( `undefined facet container for "${v}"` );
                }

                facetContainer.innerHTML = '';

                const html: string | undefined = state[ v ];
                if ( html && html.length > 0 ) {
                    facetContainer.insertAdjacentHTML( 'beforeend', state[ v ] );
                    ( facetContainer.parentElement as HTMLElement ).classList.remove( 'd-none' );
                    showFacetsBtn = true;
                } else {
                    ( facetContainer.parentElement as HTMLElement ).classList.add( 'd-none' );
                }
            }

            if ( !showFacetsBtn ) {
                this._facetsBtnDesktop.style.display = 'none';
                this._facetsBtnMobile.style.display = 'none';
            } else {
                this._facetsBtnDesktop.style.display = '';
                this._facetsBtnMobile.style.display = '';
            }
        }

        this._ctx.root.dispatchEvent( new CustomEvent( 'twc:update-filter-count-badges', { bubbles: true } ) );
    }
}

export default SearchUI;
